import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

interface SunProps {
  onClick: () => void;
  fadeAlpha: number;
}

const Sun: React.FC<SunProps> = ({ onClick, fadeAlpha }) => {
  const sunRef = useRef<THREE.Group>(null);
  const coronaRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  // Animation
  useFrame((state) => {
    if (sunRef.current) {
      sunRef.current.rotation.y += 0.005;
    }
    if (coronaRef.current) {
      coronaRef.current.rotation.z -= 0.003;
    }
    if (glowRef.current) {
      const scale = 1 + 0.1 * Math.sin(state.clock.elapsedTime * 1.5);
      glowRef.current.scale.setScalar(scale);
    }
  });

  // Shader pour la surface du soleil
  const sunSurfaceShader = useMemo(() => {
    const vertexShader = `
      varying vec2 vUv;
      varying vec3 vPosition;
      void main() {
        vUv = uv;
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      uniform float time;
      varying vec2 vUv;
      varying vec3 vPosition;

      // Fonction de bruit pour créer les granules solaires
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }

      float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);

        float a = random(i);
        float b = random(i + vec2(1.0, 0.0));
        float c = random(i + vec2(0.0, 1.0));
        float d = random(i + vec2(1.0, 1.0));

        vec2 u = f * f * (3.0 - 2.0 * f);

        return mix(a, b, u.x) +
               (c - a)* u.y * (1.0 - u.x) +
               (d - b) * u.x * u.y;
      }

      float fbm(vec2 st) {
        float value = 0.0;
        float amplitude = 0.5;
        vec2 freq = st;

        for (int i = 0; i < 5; i++) {
          value += amplitude * noise(freq);
          freq *= 2.0;
          amplitude *= 0.5;
        }
        return value;
      }

      void main() {
        // Coordonnées UV animées pour simulation convection
        vec2 animatedUv = vUv + time * 0.1;

        // Création de la texture de granules
        float granules = fbm(animatedUv * 8.0);
        float convection = fbm(animatedUv * 4.0 + vec2(time * 0.05));

        // Zones sombres (taches solaires) et zones brillantes
        float sunspots = smoothstep(0.7, 0.8, fbm(vUv * 3.0 + time * 0.02));
        float brightRegions = smoothstep(0.3, 0.6, granules);

        // Couleurs du soleil
        vec3 darkYellow = vec3(1.0, 0.7, 0.1);
        vec3 brightYellow = vec3(1.0, 0.9, 0.3);
        vec3 orange = vec3(1.0, 0.5, 0.1);
        vec3 darkOrange = vec3(0.8, 0.3, 0.05);

        // Mix des couleurs basé sur la texture
        vec3 baseColor = mix(darkYellow, brightYellow, granules);
        baseColor = mix(baseColor, orange, convection * 0.5);
        baseColor = mix(baseColor, darkOrange, sunspots);

        // Émission plus forte pour les zones brillantes
        float emission = 0.8 + brightRegions * 0.4;

        gl_FragColor = vec4(baseColor * emission, 1.0);
      }
    `;

    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 }
      },
      vertexShader,
      fragmentShader,
      transparent: false,
    });
  }, []);

  // Shader pour la couronne solaire
  const coronaShader = useMemo(() => {
    const vertexShader = `
      varying vec2 vUv;
      varying vec3 vNormal;
      void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      uniform float time;
      uniform float opacity;
      varying vec2 vUv;
      varying vec3 vNormal;

      float noise(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }

      void main() {
        vec2 st = vUv - 0.5;
        float dist = length(st);

        // Création des flammes solaires
        float angle = atan(st.y, st.x);
        float flames = sin(angle * 8.0 + time * 2.0) * 0.1;
        flames += sin(angle * 12.0 + time * 3.0) * 0.05;

        float intensity = 1.0 - smoothstep(0.3, 0.5 + flames, dist);

        // Couleurs du soleil
        vec3 color1 = vec3(1.0, 0.8, 0.2); // Jaune
        vec3 color2 = vec3(1.0, 0.4, 0.1); // Orange
        vec3 color3 = vec3(1.0, 0.2, 0.1); // Rouge

        vec3 finalColor = mix(color3, color2, intensity);
        finalColor = mix(finalColor, color1, intensity * 0.7);

        gl_FragColor = vec4(finalColor, intensity * opacity);
      }
    `;

    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        opacity: { value: 0.8 }
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
    });
  }, []);

  useFrame((state) => {
    if (sunSurfaceShader) {
      sunSurfaceShader.uniforms.time.value = state.clock.elapsedTime;
    }
    if (coronaShader) {
      coronaShader.uniforms.time.value = state.clock.elapsedTime;
      coronaShader.uniforms.opacity.value = 0.3 * fadeAlpha + 0.1 * Math.sin(state.clock.elapsedTime * 2) * fadeAlpha;
    }
  });

  const [hovered, setHovered] = useState(false);

  const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = (e: ThreeEvent<PointerEvent>) => {
    setHovered(false);
    document.body.style.cursor = 'auto';
  };

  return (
    <group
      ref={sunRef}
      scale={fadeAlpha}
      onClick={fadeAlpha > 0.1 ? onClick : undefined}
      visible={fadeAlpha > 0}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      {/* Lumière principale */}
      <pointLight
        intensity={2.0 * fadeAlpha}
        color="#ffd700"
        distance={300}
        decay={1}
      />

      {/* Hover Outline - Encompasses the aura */}
      {hovered && (
        <mesh scale={1.05}>
          <sphereGeometry args={[7.6, 32, 32]} />
          <meshBasicMaterial
            color="white"
            side={THREE.BackSide}
            transparent
            opacity={0.5}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Halo externe */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[7.6, 32, 32]} />
        <meshBasicMaterial
          color="#ffaa00"
          transparent
          opacity={0.06 * fadeAlpha}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Couronne solaire avec shader */}
      <mesh ref={coronaRef}>
        <sphereGeometry args={[7.2, 64, 64]} />
        <primitive object={coronaShader} />
      </mesh>

      {/* Corps principal du soleil */}
      <mesh>
        <sphereGeometry args={[6.5, 64, 64]} />
        <primitive object={sunSurfaceShader} />
      </mesh>

      {/* Particules internes */}
      <mesh>
        <sphereGeometry args={[5.8, 16, 16]} />
        <meshBasicMaterial
          color="#fff200"
          transparent
          opacity={0.6 * fadeAlpha}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Halo proche */}
      <mesh>
        <sphereGeometry args={[5.8, 24, 24]} />
        <meshBasicMaterial
          color="#ffdd44"
          transparent
          opacity={0.15 * fadeAlpha}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
};

export default Sun;